import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowLeft, LogOut, Mail, MessageSquare, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fetchSubmissions } from "@/lib/api";

const KEY_STORE = "alleypub_admin_key";

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [adminKey, setAdminKey] = useState("");
  const [data, setData] = useState({ contacts: [], newsletter: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      const res = await fetchSubmissions(key);
      setData(res);
      setAdminKey(key);
      setAuthed(true);
      localStorage.setItem(KEY_STORE, key);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin key.");
        localStorage.removeItem(KEY_STORE);
        setAuthed(false);
      } else {
        toast.error("Failed to load submissions.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(KEY_STORE);
    if (saved) load(saved);
  }, [load]);

  const onLogin = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    load(keyInput.trim());
  };

  const logout = () => {
    localStorage.removeItem(KEY_STORE);
    setAuthed(false);
    setAdminKey("");
    setKeyInput("");
    setData({ contacts: [], newsletter: [] });
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B132B] grid place-items-center px-5">
        <form
          data-testid="admin-login-form"
          onSubmit={onLogin}
          className="w-full max-w-sm rounded-md border border-white/10 bg-[#1C2541] p-8"
        >
          <div className="grid h-12 w-12 place-items-center rounded-full bg-primary/15 text-primary mb-6">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="font-display text-3xl text-[#F8F5F2]">Staff Login</h1>
          <p className="mt-2 text-sm text-[#8E94A3]">
            Enter the admin key to view submissions.
          </p>
          <div className="mt-6">
            <Label htmlFor="key" className="text-[#F8F5F2] font-label text-[11px]">
              Admin Key
            </Label>
            <Input
              id="key"
              type="password"
              data-testid="admin-key-input"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="••••••••"
              className="mt-2 h-12 bg-[#0B132B] border-white/10 text-[#F8F5F2] focus-visible:ring-primary"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="mt-5 w-full h-12 rounded-full font-label text-xs"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
          </Button>
          <Link
            to="/"
            data-testid="admin-back-link"
            className="mt-6 flex items-center justify-center gap-2 text-xs text-[#8E94A3] hover:text-primary transition-colors font-label"
          >
            <ArrowLeft className="h-3 w-3" /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B132B] text-[#F8F5F2]">
      <header className="border-b border-white/10 sticky top-0 bg-[#0B132B]/90 backdrop-blur-xl z-10">
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-home-link" className="text-[#8E94A3] hover:text-primary">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <span className="font-display text-xl">Alley Pub — Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="admin-refresh-btn"
              onClick={() => load(adminKey)}
              className="border-white/15 bg-transparent text-[#F8F5F2] hover:bg-white/10"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="admin-logout-btn"
              onClick={logout}
              className="border-white/15 bg-transparent text-[#F8F5F2] hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 md:px-8 py-10">
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="rounded-md border border-white/10 bg-[#1C2541] p-6">
            <div className="flex items-center gap-2 text-[#8E94A3] font-label text-[11px]">
              <MessageSquare className="h-4 w-4" /> Contact Messages
            </div>
            <div className="mt-2 font-display text-5xl text-primary">
              {data.contacts.length}
            </div>
          </div>
          <div className="rounded-md border border-white/10 bg-[#1C2541] p-6">
            <div className="flex items-center gap-2 text-[#8E94A3] font-label text-[11px]">
              <Mail className="h-4 w-4" /> Newsletter Subscribers
            </div>
            <div className="mt-2 font-display text-5xl text-primary">
              {data.newsletter.length}
            </div>
          </div>
        </div>

        <Tabs defaultValue="contacts" data-testid="admin-tabs">
          <TabsList className="bg-[#1C2541] border border-white/10">
            <TabsTrigger value="contacts" data-testid="tab-contacts">
              Contacts
            </TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="tab-newsletter">
              Newsletter
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="mt-5">
            <div className="overflow-x-auto rounded-md border border-white/10">
              <table className="w-full text-sm" data-testid="contacts-table">
                <thead className="bg-[#1C2541] text-[#8E94A3] font-label text-[11px]">
                  <tr>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Message</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {data.contacts.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-[#8E94A3]">
                        No messages yet.
                      </td>
                    </tr>
                  ) : (
                    data.contacts.map((c) => (
                      <tr key={c.id} className="border-t border-white/10 align-top">
                        <td className="px-4 py-3 font-medium">{c.name}</td>
                        <td className="px-4 py-3 text-[#F8F5F2]/80">{c.email}</td>
                        <td className="px-4 py-3 text-[#F8F5F2]/70 max-w-md">{c.message}</td>
                        <td className="px-4 py-3 text-[#8E94A3] whitespace-nowrap">
                          {fmt(c.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="newsletter" className="mt-5">
            <div className="overflow-x-auto rounded-md border border-white/10">
              <table className="w-full text-sm" data-testid="newsletter-table">
                <thead className="bg-[#1C2541] text-[#8E94A3] font-label text-[11px]">
                  <tr>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.newsletter.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-4 py-10 text-center text-[#8E94A3]">
                        No subscribers yet.
                      </td>
                    </tr>
                  ) : (
                    data.newsletter.map((n) => (
                      <tr key={n.id} className="border-t border-white/10">
                        <td className="px-4 py-3">{n.email}</td>
                        <td className="px-4 py-3 text-[#8E94A3] whitespace-nowrap">
                          {fmt(n.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
